import React from 'react';
import { Form, Upload, notification } from 'antd';
import {
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileZipOutlined,
    InboxOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useFormPath } from './index';

const MAX_UPLOAD_SIZE_MB = 10
const DEFAULT_MAX_COUNT = 10

export default (formPlugin, namespace) => {
    return {
        Renderer: (props) => {
            const { listFieldProps, buildName } = useFormPath()

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }

            if ((props.isPreviewMode || props.readOnly) && !props.data.fieldKey) {
                return null
            }

            const { data } = props
            let rules = [{ required: data.required || false, message: t`Ce champ est obligatoire` }]

            let tooltip = null
            if (data.tooltip) {
                tooltip = {
                    title: data.tooltip,
                    icon: <InfoCircleOutlined />,
                }
            }

            const multiple = data.multiple || false
            const prefix = multiple ? "[DOCUMENT_MULTI]_" : "[DOCUMENT]_"

            // Defaults restrictifs : on n'autorise pas "application/*" tout court (couvrirait .exe, .msi, etc.)
            let Icon = <InboxOutlined />
            let accept = "image/*,application/pdf"

            switch (data.type) {
                case formPlugin.UPLOAD_TYPE[0]:
                    Icon = <FileImageOutlined />
                    accept = "image/*"
                    break;
                case formPlugin.UPLOAD_TYPE[1]:
                    Icon = <FilePdfOutlined />
                    accept = "application/pdf"
                    break;
                case formPlugin.UPLOAD_TYPE[2]:
                    Icon = <FileZipOutlined />
                    accept = "application/zip,application/x-zip-compressed"
                    break;
            }

            const finalName = prefix + data.fieldKey
            const maxCount = multiple ? (Number.isFinite(data.maxCount) && data.maxCount > 0 ? data.maxCount : DEFAULT_MAX_COUNT) : 1

            const beforeUpload = (file) => {
                const sizeMB = file.size / 1024 / 1024
                if (sizeMB > MAX_UPLOAD_SIZE_MB) {
                    formPlugin.notify('error', {
                        message: t`Fichier trop volumineux`,
                        description: t`La taille maximale autorisée est de ${MAX_UPLOAD_SIZE_MB} Mo.`,
                    })
                    return Upload.LIST_IGNORE
                }
                return true
            }

            return (
                <Form.Item
                    {...(listFieldProps || {})}
                    label={(data.label || t`Fichier`) + " :"}
                    name={buildName(finalName)}
                    rules={rules}
                    tooltip={tooltip}
                >
                    <ValueToFileList multiple={multiple}>
                        <Upload.Dragger
                            multiple={multiple}
                            disabled={formPlugin.runtime?.preview}
                            maxCount={maxCount}
                            action={formPlugin.BASE_URL + "/forms/upload"}
                            data={{ formId: formPlugin.formData?.id }}
                            listType="picture"
                            accept={accept}
                            beforeUpload={beforeUpload}
                        >
                            <p className="ant-upload-drag-icon">
                                {Icon}
                            </p>
                            <p className="ant-upload-text">
                                <Trans>Cliquez ou faites glisser le fichier dans cette zone pour le télécharger</Trans>
                            </p>
                            <p className="ant-upload-hint">{data.placeholder}</p>
                        </Upload.Dragger>
                    </ValueToFileList>
                </Form.Item>
            )
        },
        namespace: namespace,
        id: 'form-upload' + namespace,
        title: t`Fichier`,
        description: t`Élément de formulaire utilisable.`,
        icon: <FileOutlined />,
        iconColor: "#F39C12",
        version: 1,
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    required: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Obligatoire`,
                        }
                    },
                    multiple: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Plusieurs Fichiers`,
                        }
                    },
                    fieldKey: {
                        type: 'string',
                        uniforms: {
                            required: true,
                            label: t`Identifiant`,
                            placeholder: t`Identifiant du Champ`,
                        }
                    },
                    label: {
                        type: 'string',
                        uniforms: {
                            required: true,
                            label: t`Étiquette`,
                            placeholder: t`Libellé`,
                        }
                    },
                    placeholder: {
                        type: 'string',
                        uniforms: {
                            label: t`Sous-Étiquette`,
                            placeholder: t`Sous-Étiquette`,
                        }
                    },
                    tooltip: {
                        type: 'string',
                        uniforms: {
                            label: t`Message d'aide`,
                            placeholder: t`Message d'aide`,
                            multiline: true,
                            rows: 2,
                        }
                    },
                    type: {
                        type: "string",
                        uniforms: {
                            label: t`Type de Fichier`,
                            allowedValues: formPlugin.UPLOAD_TYPE,
                        }
                    },
                }
            },
        },
    }
}

export function ValueToFileList({ multiple, value, onChange, children }) {

    const convertToFileList = (v) => {
        if (!v) return []
        let finalValue = multiple ? v : [v]

        return finalValue.map(i => ({
            uid: i.doc,
            name: i.name,
            response: i.doc,
            status: 'done',
        }))
    }

    const convertToValue = (e) => {
        if (multiple) {
            if (Array.isArray(e.fileList)) {
                var values = []
                for (var file of e.fileList) {
                    if (file.status === "done") {
                        values.push({ name: file.name, doc: file.response })
                    }
                }
                return values
            }
            return []
        } else {
            if (e.file && e.file.status === "done") {
                return { name: e.file.name, doc: e.file.response }
            }
            return null
        }
    }

    return React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                defaultFileList: convertToFileList(value),
                onChange: (e) => {
                    const finalValue = convertToValue(e)
                    if (finalValue) {
                        onChange(finalValue)
                    }
                }
            });
        }
        return child;
    })
}
